/**
 * Asking for a new ITEM to be created — point 14's item half.
 *
 * ---- It exists after all ----
 *
 * `uomRequest.ts` said this half could not be built: the API collection's `I8 - Item Creation
 * Request` entry has NO url, no method and no body — a placeholder. That was true of the
 * COLLECTION and false of the server. Probed by symmetry with the UOM half on 2026-09-05:
 *
 *     GET items.item_creation_request?item_name=X
 *       → 500 TypeError: item_creation_request() missing 2 required positional
 *         arguments: 'item_group' and 'item_uom'
 *     GET items.item_creation_request?item_name=X&item_group=Miscellenious&item_uom=Nos
 *       → 200 {"status":"success","name":"X"}
 *
 * The error message gave up the signature. The `Item Creation Request` document it files carries
 * `item_name`, `item_group`, `item_uom`, `status: "Open"` and a `platform_id` — read back live.
 *
 * ---- It is a REQUEST, and nothing here may imply otherwise ----
 *
 * Filing one does not create the item. The catalogue is unchanged until somebody approves it, so
 * the line the purchaser was working on still cannot be matched to it. Every string in this flow
 * says "requested", never "created" — the same rule `uomRequest.ts` follows, for the same reason:
 * a purchaser who believes the item now exists will search for it, fail, and conclude the app is
 * broken.
 *
 * ---- The docname is the NAME, so a duplicate is silent ----
 *
 * `item_creation_request` names the document after the item, upper-cased with spaces underscored
 * (`BOSUN PROBE DELETE ME` → `BOSUN_PROBE_DELETE_ME`). Requesting the same description twice
 * therefore returns `200` with the SAME name rather than an error — the second request quietly
 * overwrites the first. That is not a duplicate guard we can rely on, and it is why the caller
 * reports the returned name rather than assuming a new one was made.
 */

import { useMutation } from '@tanstack/react-query'
import { callGet } from './bosun'
import { withBosunError } from './useEnquiryMutations'
import type { BosunApiError } from './client'

export interface ItemRequestInput {
  /** The customer's own description — what the purchaser could not find in the catalogue. */
  item_name: string
  item_group: string
  item_uom: string
  /**
   * Where the request is raised from (required by the server since 2026-09-19, measured: `500
   * TypeError … missing 'reference_name', 'reference_doc', and 'row_id'`). `rowId` is the line's
   * `enquiry_row_no`, stored as `opportunity_row_id` on the request; blank from the toolbar,
   * where there is no line — accepted (measured).
   */
  enquiryId: string
  rowId?: string
}

/** The exact query the method takes. */
export function itemRequestParams(input: ItemRequestInput): Record<string, string> {
  return {
    item_name: input.item_name.trim(),
    item_group: input.item_group.trim(),
    item_uom: input.item_uom.trim(),
    reference_name: input.enquiryId,
    reference_doc: 'Enquiry',
    row_id: input.rowId ?? '',
  }
}

export interface WireItemRequestResult {
  status?: string
  /** The new `Item Creation Request` docname. */
  name?: string
}

/**
 * Why this request cannot be sent, or `null` if it can.
 *
 * All three fields are required by the endpoint — it answers `500 TypeError` on a missing
 * `item_group` or `item_uom`, and files a nameless document on a blank `item_name`. Caught here so
 * none of that reaches the purchaser as a raw server error.
 *
 * No duplicate check against the catalogue: the whole premise is that this item is NOT in it. The
 * purchaser has already searched — `CatalogPickerModal` is how they got here.
 */
export function validateItemRequest(input: Pick<ItemRequestInput, 'item_name' | 'item_group' | 'item_uom'>): string | null {
  if (input.item_name.trim() === '') return 'Enter a description for the item to request.'
  if (input.item_group.trim() === '') return 'Choose an item group — the request needs one.'
  if (input.item_uom.trim() === '') return 'Choose the unit this item is ordered in.'
  return null
}

export function useRequestItem(options?: { onSuccess?: (name: string, itemName: string) => void }) {
  return useMutation<{ name: string; itemName: string }, BosunApiError, ItemRequestInput>({
    mutationFn: withBosunError(async (input) => {
      const itemName = input.item_name.trim()
      const wire = await callGet<WireItemRequestResult>('items', 'item_creation_request', itemRequestParams(input))
      if (wire?.status !== 'success' || !wire.name) {
        throw new Error('The item request was not filed — the server did not confirm it.')
      }
      return { name: wire.name, itemName }
    }),
    onSuccess: ({ name, itemName }) => options?.onSuccess?.(name, itemName),
  })
}
